import {ListItem} from '@github-ui/list-view/ListItem'
import {ListItemActionBar} from '@github-ui/list-view/ListItemActionBar'
import {ListItemDescription} from '@github-ui/list-view/ListItemDescription'
import {ListItemLeadingContent} from '@github-ui/list-view/ListItemLeadingContent'
import {ListItemLeadingVisual} from '@github-ui/list-view/ListItemLeadingVisual'
import {ListItemMainContent} from '@github-ui/list-view/ListItemMainContent'
import {ListItemMetadata} from '@github-ui/list-view/ListItemMetadata'
import {ListItemTitle} from '@github-ui/list-view/ListItemTitle'
import {timestampDate} from '@github-ui/dotcom-schema/protobuf'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {Link as RouterLink} from '@github-ui/react-router'
import {VerifiedHTMLText} from '@github-ui/safe-html/VerifiedHTML'
import {useClickAnalytics} from '@github-ui/use-analytics'
import type {QueryKey} from '@github-ui/react-query'
import {clsx} from 'clsx'
import {
  type WorkflowRun,
  WorkflowRunActorType,
  WorkflowRunConclusion,
  type WorkflowRunHovercard,
  WorkflowRunHovercardType,
  WorkflowRunStatus,
  WorkflowRunTimingKind,
} from '@github-ui/dotcom-schema/types/actions/v1/workflow_runs_route'
import type {Icon} from '@primer/octicons-react'
import {
  AlertIcon,
  CalendarIcon,
  CheckCircleFillIcon,
  CircleIcon,
  ClockIcon,
  DotFillIcon,
  FileCodeIcon,
  GitPullRequestIcon,
  IssueReopenedIcon,
  SkipIcon,
  SquareFillIcon,
  StopIcon,
  StopwatchIcon,
  ToolsIcon,
  TrashIcon,
  XCircleFillIcon,
} from '@primer/octicons-react'
import {
  ActionList,
  AnchoredOverlay,
  BranchName,
  Button,
  Heading,
  Label,
  Link,
  RelativeTime,
  Spinner,
} from '@primer/react'
import type {RelativeTimeProps} from '@primer/react'
import {memo, useCallback, useId, useLayoutEffect, useMemo, useRef, useState, type FocusEvent} from 'react'
import {useWorkflowRunActions} from '../../hooks/workflow-runs/use-workflow-run-actions'
import {WorkflowRunDeleteDialog} from './WorkflowRunDeleteDialog'
import styles from './WorkflowRunListItem.module.css'

interface WorkflowRunListItemProps {
  run: WorkflowRun
  queryKey: QueryKey
  actionsEnabled?: boolean
  onFocusedRowRemoved?: () => void
}

// Empty day/month attributes are the custom element's supported way to disable those date parts.
const DISABLED_RELATIVE_TIME_DATE_PARTS = {day: '', month: ''} as unknown as Pick<RelativeTimeProps, 'day' | 'month'>

export const WorkflowRunListItem = memo(function WorkflowRunListItemComponent({
  run,
  queryKey,
  actionsEnabled = true,
  onFocusedRowRemoved,
}: WorkflowRunListItemProps) {
  const focusedElementRef = useRef<EventTarget | null>(null)
  const handleFocus = useCallback((event: FocusEvent<HTMLLIElement>) => {
    focusedElementRef.current = event.target
  }, [])
  useLayoutEffect(
    () => () => {
      // Focus events also bubble here from the row's portaled action menu.
      if (focusedElementRef.current === document.activeElement) onFocusedRowRemoved?.()
    },
    [onFocusedRowRemoved],
  )

  const {sendClickAnalyticsEvent} = useClickAnalytics()
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deleteErrorMessage, setDeleteErrorMessage] = useState<string>()
  const actionMenuTriggerRef = useRef<HTMLElement>(null)
  const handleDeleteError = useCallback(
    (error: Error) => {
      if (!deleteDialogOpen) return false

      setDeleteErrorMessage(error.message)
      return true
    },
    [deleteDialogOpen],
  )
  const handleDeleteSuccess = useCallback(() => {
    setDeleteDialogOpen(false)
    setDeleteErrorMessage(undefined)
  }, [])
  const {cancelMutation, deleteMutation} = useWorkflowRunActions(queryKey, {
    onDeleteError: handleDeleteError,
    onDeleteSuccess: handleDeleteSuccess,
  })
  const status = statusVisual(
    run.state?.status ?? WorkflowRunStatus.UNSPECIFIED,
    run.state?.conclusion ?? WorkflowRunConclusion.UNSPECIFIED,
  )
  const actions = run.actions
  const cancelUrl = actions?.cancelUrl
  const hasActions =
    actionsEnabled &&
    Boolean(
      actions?.cancelUrl ||
      actions?.workflowFileUrl ||
      actions?.deleteUrl ||
      actions?.stafftoolsUrl ||
      actions?.pullRequestUrl,
    )

  const sendClick = useCallback(
    (action: string, label: string) => {
      sendClickAnalyticsEvent({
        category: 'ACTIONS_WORKFLOWS',
        action,
        label: `${label};ref_loc:actions_workflow_runs`,
      })
    },
    [sendClickAnalyticsEvent],
  )

  const secondaryActions = useMemo(() => {
    if (!actions || !hasActions) return undefined

    return (
      <ListItemActionBar
        anchorRef={actionMenuTriggerRef}
        className={styles.ActionBar}
        label={`actions for workflow run ${run.runNumber}`}
        staticMenuActions={[
          ...(cancelUrl
            ? [
                {
                  key: 'cancel-run',
                  render: () => (
                    <ActionList.Item
                      variant="danger"
                      loading={cancelMutation.isPending}
                      onSelect={() => {
                        sendClick('workflow_runs.cancel', 'ref_cta:cancel_run')
                        cancelMutation.mutate(cancelUrl)
                      }}
                    >
                      <ActionList.LeadingVisual>
                        <StopIcon />
                      </ActionList.LeadingVisual>
                      Cancel run
                    </ActionList.Item>
                  ),
                },
              ]
            : []),
          ...(actions.pullRequestUrl && actions.pullRequestLabel
            ? [
                {
                  key: 'pull-request',
                  render: () => (
                    <ActionList.LinkItem
                      href={actions.pullRequestUrl}
                      onClick={() => sendClick('workflow_runs.view_pull_request', 'ref_cta:view_pull_request')}
                    >
                      <ActionList.LeadingVisual>
                        <GitPullRequestIcon />
                      </ActionList.LeadingVisual>
                      {actions.pullRequestLabel}
                    </ActionList.LinkItem>
                  ),
                },
              ]
            : []),
          ...(actions.workflowFileUrl
            ? [
                {
                  key: 'workflow-file',
                  render: () => (
                    <ActionList.LinkItem
                      href={actions.workflowFileUrl}
                      onClick={() => sendClick('workflow_runs.view_workflow_file', 'ref_cta:view_workflow_file')}
                    >
                      <ActionList.LeadingVisual>
                        <FileCodeIcon />
                      </ActionList.LeadingVisual>
                      View workflow file
                    </ActionList.LinkItem>
                  ),
                },
              ]
            : []),
          ...(actions.deleteUrl
            ? [
                {
                  key: 'delete-run',
                  render: () => (
                    <ActionList.Item
                      variant="danger"
                      onSelect={() => {
                        sendClick('workflow_runs.delete_open', 'ref_cta:delete_workflow_run')
                        setDeleteErrorMessage(undefined)
                        setDeleteDialogOpen(true)
                      }}
                    >
                      <ActionList.LeadingVisual>
                        <TrashIcon />
                      </ActionList.LeadingVisual>
                      Delete workflow run
                    </ActionList.Item>
                  ),
                },
              ]
            : []),
          ...(actions.stafftoolsUrl && actions.stafftoolsLabel
            ? [
                {key: 'stafftools-divider', render: () => <ActionList.Divider />},
                {
                  key: 'stafftools',
                  render: () => (
                    <ActionList.LinkItem
                      href={actions.stafftoolsUrl}
                      onClick={() => sendClick('workflow_runs.view_stafftools', 'ref_cta:view_stafftools')}
                    >
                      <ActionList.LeadingVisual>
                        <ToolsIcon />
                      </ActionList.LeadingVisual>
                      {actions.stafftoolsLabel}
                    </ActionList.LinkItem>
                  ),
                },
              ]
            : []),
        ]}
      />
    )
  }, [actions, cancelMutation, cancelUrl, hasActions, run.runNumber, sendClick])

  if (run.isDisruptive) {
    const disruptiveRunLabel = `This workflow run has been marked as disruptive. Workflow run ID ${run.id}.`

    return (
      <ListItem
        onFocus={handleFocus}
        aria-label={disruptiveRunLabel}
        className={clsx(styles.ListItem, styles.DisruptiveListItem)}
        title={
          <ListItemTitle
            headingClassName={styles.DisruptiveTitle}
            value="This workflow run has been marked as disruptive"
          />
        }
      >
        <ListItemLeadingContent>
          <ListItemLeadingVisual icon={TrashIcon} color="var(--fgColor-muted)" description="Disruptive workflow run" />
        </ListItemLeadingContent>
      </ListItem>
    )
  }

  const title = run.title || run.name || '(Unknown event)'
  const workflowName = run.workflowName || run.name
  const closeDeleteDialog = () => {
    sendClick('workflow_runs.delete_cancel', 'ref_cta:cancel_delete_workflow_run')
    setDeleteDialogOpen(false)
    setDeleteErrorMessage(undefined)
    requestAnimationFrame(() => actionMenuTriggerRef.current?.focus())
  }

  return (
    <>
      <ListItem
        onFocus={handleFocus}
        aria-label={workflowRunAriaLabel(status.ariaLabel, run.runNumber, workflowName, title)}
        className={styles.ListItem}
        metadataContainerClassName={clsx(styles.MetadataContainer, 'flex-items-start')}
        title={
          <ListItemTitle
            value={run.titleHtml ? <VerifiedHTMLText as="span" html={run.titleHtml} /> : title}
            href={run.runUrl}
            linkProps={{as: RouterLink, to: run.runUrl}}
            headingClassName={styles.Title}
            onClick={() => sendClick('workflow_runs.open_run', 'ref_cta:workflow_run_title')}
          />
        }
        metadata={<WorkflowRunMetadata run={run} sendClick={sendClick} />}
        secondaryActions={secondaryActions}
      >
        <ListItemLeadingContent>
          <ListItemLeadingVisual icon={status.icon} color={status.color} description={status.ariaLabel}>
            {status.spinner && <Spinner className="color-fg-attention" size="small" srText={null} />}
          </ListItemLeadingVisual>
        </ListItemLeadingContent>
        <ListItemMainContent className={styles.MainContent}>
          <WorkflowRunDescription run={run} sendClick={sendClick} />
        </ListItemMainContent>
      </ListItem>
      {actionsEnabled && deleteDialogOpen && actions?.deleteUrl && (
        <WorkflowRunDeleteDialog
          deleteUrl={actions.deleteUrl}
          warningUrl={actions.deleteWarningUrl}
          deleting={deleteMutation.isPending}
          errorMessage={deleteErrorMessage}
          onCancel={closeDeleteDialog}
          onConfirm={deleteUrl => {
            sendClick('workflow_runs.delete_confirm', 'ref_cta:confirm_delete_workflow_run')
            setDeleteErrorMessage(undefined)
            deleteMutation.mutate(deleteUrl)
          }}
        />
      )}
    </>
  )
})

function WorkflowRunDescription({
  run,
  sendClick,
}: {
  run: WorkflowRun
  sendClick: (action: string, label: string) => void
}) {
  const description = run.description
  const getHovercardAttributesForType = useGetHovercardAttributesForType()
  const actorLabel = workflowRunActorLabel(description?.actorType)
  const hasDescriptionDetails = [
    description?.primaryText,
    description?.linkText,
    description?.action,
    description?.showActor && description.actorName,
    description?.showSpammyLabel,
  ].some(Boolean)
  const runNumberLabel = `#${run.runNumber}${hasDescriptionDetails ? ':' : ''}`

  return (
    <ListItemDescription className="d-flex flex-wrap flex-items-center gap-1 color-fg-muted">
      <strong>{run.workflowName || run.name}</strong>
      <span>{runNumberLabel}</span>
      {description?.primaryText && <span>{description.primaryText}</span>}
      {description?.linkText &&
        (description.linkUrl ? (
          <Link
            as={RouterLink}
            inline
            to={description.linkUrl}
            {...workflowRunHovercardAttributes(getHovercardAttributesForType, description.triggerHovercard)}
            onClick={() => sendClick('workflow_runs.open_trigger', 'ref_cta:workflow_run_trigger')}
          >
            {description.linkText}
          </Link>
        ) : (
          <span>{description.linkText}</span>
        ))}
      {description?.action && <span>{description.action}</span>}
      {description?.showActor && description.actorName && (
        <>
          <span>by</span>
          {description.actorUrl ? (
            <Link
              as={RouterLink}
              inline
              to={description.actorUrl}
              {...workflowRunHovercardAttributes(getHovercardAttributesForType, description.actorHovercard)}
              onClick={() => sendClick('workflow_runs.open_actor', 'ref_cta:workflow_run_actor')}
            >
              {description.actorName}
            </Link>
          ) : (
            <span>{description.actorName}</span>
          )}
          {actorLabel && <Label variant="secondary">{actorLabel}</Label>}
        </>
      )}
      {description?.showSpammyLabel && <Label variant="danger">Spammy</Label>}
    </ListItemDescription>
  )
}

function workflowRunActorLabel(actorType: WorkflowRunActorType | undefined) {
  switch (actorType) {
    case WorkflowRunActorType.AI:
      return 'AI'
    case WorkflowRunActorType.BOT:
      return 'Bot'
    case WorkflowRunActorType.MANNEQUIN:
      return 'mannequin'
    default:
      return undefined
  }
}

function workflowRunHovercardAttributes(
  getHovercardAttributesForType: ReturnType<typeof useGetHovercardAttributesForType>,
  hovercard?: WorkflowRunHovercard,
) {
  switch (hovercard?.type) {
    case WorkflowRunHovercardType.USER:
      return getHovercardAttributesForType('user', {url: hovercard.url})
    case WorkflowRunHovercardType.PULL_REQUEST:
      return getHovercardAttributesForType('pull_request', {url: hovercard.url})
    case WorkflowRunHovercardType.ISSUE:
      return getHovercardAttributesForType('issue', {url: hovercard.url})
    case WorkflowRunHovercardType.COPILOT:
      return getHovercardAttributesForType('copilot', {url: hovercard.url})
    default:
      return {}
  }
}

function WorkflowRunMetadata({run, sendClick}: {run: WorkflowRun; sendClick: (action: string, label: string) => void}) {
  const createdAt = run.createdAt?.timestamp ? timestampDate(run.createdAt.timestamp) : undefined

  return (
    <>
      {run.branch && (
        <ListItemMetadata className={clsx(styles.Metadata, styles.BranchMetadata, 'd-flex flex-items-center gap-2')}>
          <BranchName
            href={run.branch.url}
            className={styles.Branch}
            onClick={() => sendClick('workflow_runs.open_branch', 'ref_cta:workflow_run_branch')}
          >
            {run.branch.title}
          </BranchName>
        </ListItemMetadata>
      )}
      {!run.branch && (
        <ListItemMetadata
          aria-hidden="true"
          className={clsx(styles.Metadata, styles.BranchPlaceholder, 'd-flex flex-items-center gap-2')}
        />
      )}
      {(createdAt || run.timing) && (
        <ListItemMetadata
          className={clsx(styles.Metadata, styles.TimingMetadata, 'd-flex flex-column flex-items-start gap-1 pt-md-2')}
        >
          {createdAt && (
            <span className="d-inline-flex flex-items-center gap-1">
              <CalendarIcon className="flex-shrink-0 color-fg-muted" aria-hidden="true" />
              <WorkflowRunCreatedAt
                datetime={createdAt.toISOString()}
                isToday={run.createdAt?.isToday ?? false}
                isCurrentYear={run.createdAt?.isCurrentYear ?? false}
              />
            </span>
          )}
          {run.timing &&
            (run.timing.kind === WorkflowRunTimingKind.DURATION_DETAILS && run.timing.detailsUrl ? (
              <WorkflowRunDurationDetails
                detailsUrl={run.timing.detailsUrl}
                text={run.timing.text}
                onDetailsClick={() => sendClick('workflow_runs.open_duration_details', 'ref_cta:run_duration_details')}
              />
            ) : (
              <span className="d-inline-flex flex-items-center gap-1">
                <StopwatchIcon className="flex-shrink-0 color-fg-muted" aria-hidden="true" />
                <span className={run.timing.kind === WorkflowRunTimingKind.IN_PROGRESS ? 'text-italic' : undefined}>
                  {run.timing.text}
                </span>
              </span>
            ))}
        </ListItemMetadata>
      )}
    </>
  )
}

export function WorkflowRunCreatedAt({
  datetime,
  isToday,
  isCurrentYear,
}: {
  datetime: string
  isToday: boolean
  isCurrentYear: boolean
}) {
  if (isToday) {
    return (
      <RelativeTime
        {...DISABLED_RELATIVE_TIME_DATE_PARTS}
        datetime={datetime}
        threshold="PT1H"
        hour="numeric"
        minute="numeric"
        prefix="Today at "
      />
    )
  }

  const absoluteDate = (
    <RelativeTime
      datetime={datetime}
      threshold="PT1H"
      hour="numeric"
      minute="numeric"
      month="short"
      day="numeric"
      prefix=""
      timeZoneName="short"
    />
  )

  if (isCurrentYear) return absoluteDate

  return (
    <RelativeTime
      datetime={datetime}
      threshold="PT1H"
      hour="numeric"
      minute="numeric"
      month="short"
      day="numeric"
      year="numeric"
      prefix=""
      timeZoneName="short"
    />
  )
}

function WorkflowRunDurationDetails({
  detailsUrl,
  text,
  onDetailsClick,
}: {
  detailsUrl: string
  text: string
  onDetailsClick: () => void
}) {
  const [open, setOpen] = useState(false)
  const headingId = useId()

  return (
    <AnchoredOverlay
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      renderAnchor={props => (
        <Button
          {...props}
          className="px-0 color-fg-muted text-normal"
          leadingVisual={StopwatchIcon}
          size="small"
          variant="invisible"
          aria-label={`${text}. Show run duration details`}
        >
          {text}
        </Button>
      )}
      overlayProps={{role: 'dialog', 'aria-labelledby': headingId}}
      width="medium"
    >
      <div className="p-3">
        <Heading as="h3" id={headingId} className="mb-2" variant="small">
          Run duration
        </Heading>
        <p>This is the workflow execution time which may be different than the billed time.</p>
        <Link as={RouterLink} inline to={detailsUrl} onClick={onDetailsClick}>
          See details
        </Link>
      </div>
    </AnchoredOverlay>
  )
}

function statusVisual(
  status: WorkflowRunStatus,
  conclusion: WorkflowRunConclusion,
): {icon?: Icon; color: string; ariaLabel: string; spinner?: boolean} {
  if (status === WorkflowRunStatus.COMPLETED) {
    switch (conclusion) {
      case WorkflowRunConclusion.NEUTRAL:
        return {icon: SquareFillIcon, color: 'var(--fgColor-muted)', ariaLabel: 'neutral: '}
      case WorkflowRunConclusion.SUCCESS:
        return {icon: CheckCircleFillIcon, color: 'var(--fgColor-success)', ariaLabel: 'completed successfully: '}
      case WorkflowRunConclusion.FAILURE:
        return {icon: XCircleFillIcon, color: 'var(--fgColor-danger)', ariaLabel: 'failed: '}
      case WorkflowRunConclusion.CANCELLED:
        return {icon: StopIcon, color: 'var(--fgColor-muted)', ariaLabel: 'cancelled: '}
      case WorkflowRunConclusion.ACTION_REQUIRED:
        return {
          icon: AlertIcon,
          color: 'var(--fgColor-attention)',
          ariaLabel: 'requires action with the application: ',
        }
      case WorkflowRunConclusion.TIMED_OUT:
        return {icon: XCircleFillIcon, color: 'var(--fgColor-danger)', ariaLabel: 'timed out: '}
      case WorkflowRunConclusion.SKIPPED:
        return {icon: SkipIcon, color: 'var(--fgColor-muted)', ariaLabel: 'skipped: '}
      case WorkflowRunConclusion.STALE:
        return {
          icon: CircleIcon,
          color: 'var(--fgColor-muted)',
          ariaLabel: 'marked stale by GitHub because it took too long: ',
        }
      case WorkflowRunConclusion.STARTUP_FAILURE:
        return {icon: XCircleFillIcon, color: 'var(--fgColor-danger)', ariaLabel: 'failed at startup: '}
      default:
        return {icon: IssueReopenedIcon, color: 'var(--fgColor-muted)', ariaLabel: 'status unavailable: '}
    }
  }

  switch (status) {
    case WorkflowRunStatus.REQUESTED:
      return {icon: CircleIcon, color: 'var(--fgColor-muted)', ariaLabel: 'requested: '}
    case WorkflowRunStatus.PENDING:
      return {
        icon: ClockIcon,
        color: 'var(--fgColor-attention)',
        ariaLabel: 'waiting for another serialized run to finish: ',
      }
    case WorkflowRunStatus.WAITING:
      return {icon: ClockIcon, color: 'var(--fgColor-attention)', ariaLabel: 'waiting: '}
    case WorkflowRunStatus.QUEUED:
      return {icon: DotFillIcon, color: 'var(--fgColor-attention)', ariaLabel: 'queued: '}
    case WorkflowRunStatus.IN_PROGRESS:
      return {color: 'var(--fgColor-attention)', ariaLabel: 'currently running: ', spinner: true}
    default:
      return {icon: IssueReopenedIcon, color: 'var(--fgColor-muted)', ariaLabel: 'status unavailable: '}
  }
}

function workflowRunAriaLabel(statusAriaLabel: string, runNumber: number, workflowName: string, title: string) {
  const runLabel = `${statusAriaLabel}Run ${runNumber} of ${workflowName}.`
  return title === workflowName ? runLabel : `${runLabel} ${title}`
}
